<template>
    <BaseDialog v-model:open="isOpen" :title="t('connections.merge.title')">
        <template v-if="$slots.trigger" #trigger>
            <slot name="trigger"></slot>
        </template>
        <p class="body-small text-ink-muted">
            {{ isAnchored ? t("connections.merge.descriptionAnchored", { name: currentName }) : t("connections.merge.description") }}
        </p>

        <!-- Step 1: Select connections -->
        <div v-if="!showConfirmation" class="flex flex-col gap-4">
            <!-- Anchored mode: current person is fixed, pick who to merge with + which to keep -->
            <template v-if="isAnchored">
                <div class="flex flex-col gap-2">
                    <span class="body-small-semibold text-control-label">{{ t("connections.merge.mergeWithLabel", { name: currentName }) }}</span>
                    <BaseSelect
                        :model-value="otherId"
                        :options="otherOptions"
                        :placeholder="isLoadingCandidates ? t('connections.merge.loadingPeople') : t('connections.merge.selectPersonPlaceholder')"
                        trigger-class="w-full!"
                        :aria-label="t('connections.merge.selectPersonLabel')"
                        @update:model-value="handleOtherChange" />
                </div>
                <div v-if="otherId" class="flex flex-col gap-2">
                    <span class="body-small-semibold text-control-label">{{ t("connections.merge.keepQuestion") }}</span>
                    <BaseSelect
                        :model-value="keepId"
                        :options="keepOptions"
                        :placeholder="t('connections.merge.keepRecordPlaceholder')"
                        trigger-class="w-full!"
                        :aria-label="t('connections.merge.keepRecordLabel')"
                        @update:model-value="(val: string) => (keepId = val)" />
                </div>
            </template>

            <!-- List mode: pick both sides freely -->
            <template v-else>
                <div class="flex flex-col gap-2">
                    <span class="body-small-semibold text-control-label">{{ t("connections.merge.targetLabel") }}</span>
                    <BaseSelect
                        :model-value="targetId"
                        :options="candidateOptions"
                        :placeholder="t('connections.merge.targetPlaceholder')"
                        trigger-class="w-full!"
                        :aria-label="t('connections.merge.targetAriaLabel')"
                        @update:model-value="handleTargetChange" />
                </div>
                <div class="flex flex-col gap-2">
                    <span class="body-small-semibold text-control-label">{{ t("connections.merge.sourceLabel") }}</span>
                    <BaseSelect
                        :model-value="sourceId"
                        :options="sourceOptions"
                        :placeholder="t('connections.merge.sourcePlaceholder')"
                        trigger-class="w-full!"
                        :aria-label="t('connections.merge.sourceAriaLabel')"
                        @update:model-value="handleSourceChange" />
                </div>
            </template>

            <template v-if="resolvedKeepId && resolvedRemoveId">
                <!-- Direction diagram: the person being removed (left) merges INTO the kept person (right). -->
                <div class="flex items-stretch gap-2 sm:gap-3">
                    <div class="flex-1 min-w-0 rounded-lg border border-status-error bg-status-error-soft p-3 flex flex-col items-center text-center gap-2">
                        <PeopleAvatar :name="removeName" />
                        <p class="body-small-semibold truncate w-full">{{ removeName }}</p>
                        <span class="text-[11px] uppercase tracking-wide text-button-danger-hover font-semibold">{{ t("connections.merge.willBeRemoved") }}</span>
                    </div>
                    <div class="flex flex-col items-center justify-center shrink-0 gap-1">
                        <BaseButton
                            theme="neutral"
                            size="sm"
                            icon-only
                            class="group"
                            :title="t('connections.merge.swapLabel')"
                            :aria-label="t('connections.merge.swapLabel')"
                            @click="swapDirection">
                            <i class="bi bi-arrow-right text-lg group-hover:hidden" aria-hidden="true"></i>
                            <i class="bi bi-arrow-left-right text-lg hidden group-hover:inline" aria-hidden="true"></i>
                        </BaseButton>
                        <span class="text-[10px] text-ink-muted leading-none">{{ t("connections.merge.swap") }}</span>
                    </div>
                    <div class="flex-1 min-w-0 rounded-lg border border-border bg-surface-sunken p-3 flex flex-col items-center text-center gap-2">
                        <PeopleAvatar :name="keepName" />
                        <p class="body-small-semibold truncate w-full">{{ keepName }}</p>
                        <span class="text-[11px] uppercase tracking-wide text-ink font-semibold">{{ t("connections.merge.willBeKept") }}</span>
                    </div>
                </div>
                <div class="bg-surface-sunken border border-border rounded-lg p-3 flex flex-col gap-2">
                    <p class="body-small text-ink">
                        <i class="bi bi-journal-text mr-1 text-ink-muted" aria-hidden="true"></i>
                        <I18nT keypath="connections.merge.summaryNotesMerged">
                            <template #removed
                                ><strong>{{ removeName }}</strong></template
                            >
                            <template #kept
                                ><strong>{{ keepName }}</strong></template
                            >
                        </I18nT>
                    </p>
                    <p class="body-small text-ink">
                        <i class="bi bi-person-check mr-1 text-ink-muted" aria-hidden="true"></i>
                        <I18nT keypath="connections.merge.summaryDetailsKept">
                            <template #kept
                                ><strong>{{ keepName }}</strong></template
                            >
                        </I18nT>
                    </p>
                    <p class="body-small text-button-danger-hover">
                        <i class="bi bi-exclamation-triangle mr-1" aria-hidden="true"></i>
                        <I18nT keypath="connections.merge.summaryRemoved">
                            <template #removed
                                ><strong>{{ removeName }}</strong></template
                            >
                        </I18nT>
                    </p>
                </div>
            </template>
        </div>

        <!-- Step 2: Confirm -->
        <div v-else class="flex flex-col gap-4">
            <!-- Same direction diagram as the selection step: removed (left) → kept (right). -->
            <div class="flex items-stretch gap-2 sm:gap-3">
                <div class="flex-1 min-w-0 rounded-lg border border-status-error bg-status-error-soft p-3 flex flex-col items-center text-center gap-2">
                    <PeopleAvatar :name="removeName" />
                    <p class="body-small-semibold truncate w-full">{{ removeName }}</p>
                    <span class="text-[11px] uppercase tracking-wide text-button-danger-hover font-semibold">{{ t("connections.merge.willBeRemoved") }}</span>
                </div>
                <div class="flex flex-col items-center justify-center text-ink-muted shrink-0" aria-hidden="true">
                    <i class="bi bi-arrow-right text-2xl" aria-hidden="true"></i>
                </div>
                <div class="flex-1 min-w-0 rounded-lg border border-border bg-surface-sunken p-3 flex flex-col items-center text-center gap-2">
                    <PeopleAvatar :name="keepName" />
                    <p class="body-small-semibold truncate w-full">{{ keepName }}</p>
                    <span class="text-[11px] uppercase tracking-wide text-ink font-semibold">{{ t("connections.merge.willBeKept") }}</span>
                </div>
            </div>
            <I18nT keypath="connections.merge.confirmText" tag="p" class="body-small text-ink-muted">
                <template #removed
                    ><strong>{{ removeName }}</strong></template
                >
                <template #kept
                    ><strong>{{ keepName }}</strong></template
                >
            </I18nT>
            <BaseNotification v-if="errorMessage" theme="error" hide-dismiss :text="errorMessage" />
        </div>
        <template #footer>
            <template v-if="!showConfirmation">
                <BaseButton theme="neutral" @click="isOpen = false">{{ t("common.cancel") }}</BaseButton>
                <BaseButton theme="primary" :disabled="!canContinue" @click="showConfirmation = true">{{ t("connections.merge.continue") }}</BaseButton>
            </template>
            <template v-else>
                <BaseButton theme="neutral" :disabled="isMerging" @click="handleConfirmBack">{{
                    startAtConfirmation ? t("common.cancel") : t("connections.merge.back")
                }}</BaseButton>
                <BaseButton theme="danger" :disabled="isMerging" @click="handleMerge">
                    {{ isMerging ? t("connections.merge.merging") : t("connections.merge.confirm") }}
                </BaseButton>
            </template>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import type { ConnectionDict } from "~vue/types/connection";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { computed, ref, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

interface Props {
    // List mode: caller supplies the full set of connections to choose from.
    connections?: Array<ConnectionDict>;
    // Anchored mode: one connection is fixed (e.g. the person detail page) and the
    // candidate list is fetched on demand.
    currentConnection?: ConnectionDict;
    // List mode: pre-fill the keep/remove sides when opened programmatically
    // (e.g. drag-and-drop on the people grid). Applied each time the dialog opens.
    initialTargetId?: string;
    initialSourceId?: string;
    // Skip the selection step and open directly on the confirmation step. Used by the
    // drag-and-drop flow, where the two people are already chosen by the gesture.
    startAtConfirmation?: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();

const emit = defineEmits<{
    merged: [result: { keptId: string; removedId: string }];
}>();

const isOpen = defineModel<boolean>("open", { default: false });
const isAnchored = computed(() => !!props.currentConnection);

const labelFor = (c: ConnectionDict) => c.persona?.name || c.connection?.job_title || t("connections.merge.unnamed");

// Candidate connections: provided by the caller (list mode) or fetched (anchored mode).
const { data: fetchedConnections, execute: fetchCandidates, isFetching: isLoadingCandidates } = useFetch("/api/connections/", { immediate: false }).get().json<ConnectionDict[]>();

const candidates = computed<ConnectionDict[]>(() => props.connections ?? fetchedConnections.value ?? []);

// Used to resolve names for either side, including the anchored current connection
// (which may not appear in the candidate list while it is still loading).
const lookup = computed<ConnectionDict[]>(() => {
    const list = [...candidates.value];
    if (props.currentConnection && !list.some((c) => c.id === props.currentConnection!.id)) {
        list.push(props.currentConnection);
    }
    return list;
});
const nameById = (id: string) => {
    const c = lookup.value.find((x) => x.id === id);
    return c ? labelFor(c) : "";
};

// --- List mode state ---
const targetId = ref("");
const sourceId = ref("");

const candidateOptions = computed(() => candidates.value.map((c) => ({ key: c.id, label: labelFor(c) })));
const sourceOptions = computed(() => candidateOptions.value.filter((opt) => opt.key !== targetId.value));

const handleTargetChange = (val: string) => {
    targetId.value = val;
    if (sourceId.value === val) {
        sourceId.value = "";
    }
};
const handleSourceChange = (val: string) => {
    sourceId.value = val;
};

// --- Anchored mode state ---
const otherId = ref("");
const keepId = ref("");

const otherOptions = computed(() => candidates.value.filter((c) => c.id !== props.currentConnection?.id).map((c) => ({ key: c.id, label: labelFor(c) })));
const keepOptions = computed(() => {
    if (!props.currentConnection || !otherId.value) {
        return [];
    }
    return [
        { key: props.currentConnection.id, label: t("connections.merge.currentlyViewing", { name: labelFor(props.currentConnection) }) },
        { key: otherId.value, label: nameById(otherId.value) },
    ];
});

const handleOtherChange = (val: string) => {
    otherId.value = val;
    // Default to keeping the person the user is currently viewing.
    keepId.value = props.currentConnection?.id ?? "";
};

// --- Resolved keep / remove (shared by both modes) ---
const resolvedKeepId = computed(() => (isAnchored.value ? keepId.value : targetId.value));
const resolvedRemoveId = computed(() => {
    if (!isAnchored.value) {
        return sourceId.value;
    }
    return [props.currentConnection?.id ?? "", otherId.value].find((id) => id && id !== keepId.value) ?? "";
});

// Keep the template references simple by aliasing to the resolved values.
const keepName = computed(() => nameById(resolvedKeepId.value));
const removeName = computed(() => nameById(resolvedRemoveId.value));
const currentName = computed(() => (props.currentConnection ? labelFor(props.currentConnection) : ""));

const canContinue = computed(() => !!resolvedKeepId.value && !!resolvedRemoveId.value && resolvedKeepId.value !== resolvedRemoveId.value);

// Flip which record is kept vs removed. In anchored mode this just moves `keepId` to the
// other party; in list mode it swaps the two dropdown selections.
const swapDirection = () => {
    if (!resolvedKeepId.value || !resolvedRemoveId.value) {
        return;
    }
    if (isAnchored.value) {
        keepId.value = resolvedRemoveId.value;
    } else {
        [targetId.value, sourceId.value] = [sourceId.value, targetId.value];
    }
};

const showConfirmation = ref(false);
const isMerging = ref(false);
const errorMessage = ref("");

const resetState = () => {
    targetId.value = "";
    sourceId.value = "";
    otherId.value = "";
    keepId.value = "";
    showConfirmation.value = false;
    errorMessage.value = "";
};

watch(isOpen, (open) => {
    if (open) {
        resetState();
        if (!props.connections && !fetchedConnections.value) {
            fetchCandidates();
        }
        if (!isAnchored.value) {
            targetId.value = props.initialTargetId ?? "";
            sourceId.value = props.initialSourceId ?? "";
            if (props.startAtConfirmation && targetId.value && sourceId.value) {
                showConfirmation.value = true;
            }
        }
    }
});

// In the drag flow there is no selection step to return to, so the secondary button
// cancels and closes the dialog. Otherwise it steps back to the person picker.
const handleConfirmBack = () => {
    if (props.startAtConfirmation) {
        isOpen.value = false;
    } else {
        showConfirmation.value = false;
    }
};

async function handleMerge() {
    isMerging.value = true;
    errorMessage.value = "";

    const { error } = await useFetch("/api/connections/merge/").post({ target_id: resolvedKeepId.value, source_id: resolvedRemoveId.value }).json();

    isMerging.value = false;

    if (error.value) {
        if (!isSessionEndedError(error.value)) {
            errorMessage.value = t("connections.merge.error");
        }
        return;
    }

    const result = { keptId: resolvedKeepId.value, removedId: resolvedRemoveId.value };
    isOpen.value = false;
    emit("merged", result);
}
</script>
